import { ExportOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Space, Tabs } from 'antd';
import BreadCrumbs from 'common/breadcrumbs';
import PageTitle from 'common/page-title';
import PageWrapper from 'common/page-wrapper';
import Table from 'common/table/table';
import { useState } from 'react';
import { useGetCaseTypeOverviewQuery } from 'services/case-type/case-type-api';
import { CaseType, Unit } from 'services/case-type/types';
import CaseTypeDetails from './case-type-details';
import { referenceTabItems } from './reference-tables';

// If this differs per environment, source it from ClientConfigurationMiddleware
// instead and keep this as a fallback.
const CASETYPES_SHAREPOINT_URL = 'https://efsa815.sharepoint.com/:x:/r/sites/EFSACloudGovernance/_layouts/15/Doc.aspx?sourcedoc=%7BDAE6BEDF-1441-4DA8-97C0-13C0C9DDDA81%7D&file=DLW%20-%20EFSA%20TRI%20-%20Manual%20Upload%20Azure%20-%20Case%20Types%20V3.0%20-%20PCode%20-%20OPEN.xlsx&action=default&mobileredirect=true';

const filterKeys = [
  'caseTypeId',
  'caseName',
  'processTypeDescription',
  'foodDomainDescription',
];

const CaseTypes = () => {
  const [selectedCaseType, setSelectedCaseType] = useState<string>();

  const caseTypeColumns = [
    {
      title: 'CaseType Nr',
      dataIndex: 'caseTypeId',
      width: 50,
      filterSearch: true,
      sorter: {
        compare: (a: CaseType, b: CaseType) => a.caseTypeId - b.caseTypeId,
      },
    },
    {
      title: 'CaseType Name',
      dataIndex: 'caseName',
      width: 150,
      filterSearch: true,
      sorter: {
        compare: (a: CaseType, b: CaseType) =>
          a.caseName.localeCompare(b.caseName),
      },
    },
    {
      title: 'Process Type',
      dataIndex: 'processTypeDescription',
      filterSearch: true,
      sorter: {
        compare: (a: CaseType, b: CaseType) =>
          a.processTypeDescription.localeCompare(b.processTypeDescription),
      },
    },
    {
      title: 'Food Domain',
      dataIndex: 'foodDomainDescription',
      sorter: {
        compare: (a: CaseType, b: CaseType) =>
          a.foodDomainDescription.localeCompare(b.foodDomainDescription),
      },
    },
    {
      title: 'Authorization Type',
      dataIndex: 'authorisationTypeDescription',
      sorter: {
        compare: (a: CaseType, b: CaseType) =>
          a.authorisationTypeDescription.localeCompare(
            b.authorisationTypeDescription
          ),
      },
    },
    {
      title: 'Application Type',
      dataIndex: 'applicationTypeDescription',
      sorter: {
        compare: (a: CaseType, b: CaseType) =>
          a.applicationTypeDescription.localeCompare(
            b.applicationTypeDescription
          ),
      },
    },
    {
      title: 'Leading Unit',
      dataIndex: 'unit',
      sorter: {
        compare: (a: CaseType, b: CaseType) =>
          (a.unit?.departmentName ?? '').localeCompare(
            b.unit?.departmentName ?? ''
          ),
      },
      render: (unit?: Unit) => unit?.departmentName ?? '',
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      width: 150,
      render: (value: string) => (
        <Space size="middle">
          <Button
            icon={<RightOutlined />}
            onClick={() => {
              setSelectedCaseType(value);
            }}
          >
            Open Details
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageWrapper backgroundColor="white">
        <div>
          <BreadCrumbs />
          <PageTitle text="CaseType Overview" />

          <Tabs
            defaultActiveKey="casetypes"
            items={[
              {
                key: 'casetypes',
                label: 'Case Types',
                children: (
                  <Table
                    scroll={{ x: true }}
                    actionButtons={[
                      <Button
                        key="sharepoint"
                        type="primary"
                        icon={<ExportOutlined />}
                        href={CASETYPES_SHAREPOINT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in SharePoint
                      </Button>,
                    ]}
                    style={{ maxWidth: '1600px' }}
                    query={useGetCaseTypeOverviewQuery}
                    columns={caseTypeColumns}
                    endpoint=""
                    rowKey="caseTypeId"
                    pageSize={7}
                    filterOptions={{
                      keys: filterKeys,
                      placeholder:
                        'Filter by number, name, process type or food domain',
                    }}
                  />
                ),
              },
              ...referenceTabItems,
            ]}
          />
        </div>
      </PageWrapper>
      {selectedCaseType && (
        <CaseTypeDetails
          selectedCaseType={selectedCaseType.toString()}
          close={() => {
            setSelectedCaseType('');
          }}
        />
      )}
    </>
  );
};

export default CaseTypes;